import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * @type {Boolean|String}
   * @default false
   */
  active: booleanAttribute(),
  /**
   * @type {Boolean}
   * @default false
   */
  ignoreActiveState: {
    type: Boolean,
    default: false,
  },
  /**
   * Visually mark the row as a combobox's pending choice — an
   * active-descendant-style hint (same background as hover / keyboard focus)
   * shown while real focus stays elsewhere, e.g. in a search input whose Enter
   * key will pick this row. Purely visual: unlike `active` it implies no
   * committed selection, and unlike focus it never moves the roving tabindex.
   *
   * @type {Boolean|String}
   * @default false
   */
  highlighted: booleanAttribute(),
  /**
   * @type {String|Number}
   * @default undefined
   */
  title: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * @type {String|Number}
   * @default undefined
   */
  subtitle: {
    type: [String, Number],
    default: undefined,
  },
  /**
   * @type {String|Number|Boolean|Object|Array}
   * @default undefined
   */
  value: {
    type: [String, Number, Boolean, Object, Array],
    default: undefined,
  },
  /**
   * @type {Boolean|String}
   * @default false
   */
  loading: booleanAttribute(),
  /**
   * Icon to prepend to the list item.
   *
   * Either a plain `sp-icon` name (String), or a structured, domain-free icon
   * descriptor: `{ name, color, background: { shape, color } }`.
   * - `name`   — sp-icon name (resolved against the per-client icon sprite).
   * - `color`  — optional glyph fill (forwarded to sp-icon as `fill-color`).
   * - `background` — optional; when present the icon is wrapped in a decoration
   *   element. `shape` is a semantic enum (`circle` | `rounded` | `square`) that
   *   maps to a radius token; `color` is applied verbatim as the background.
   *   Omit `background` for no decoration.
   *
   * @type {String|Object}
   * @default undefined
   */
  prependIcon: {
    type: [String, Object],
    default: undefined,
  },
  /**
   * Read-only tag/chip labels rendered as a row beneath the title (and subtitle).
   * Purely presentational — clicking the row still selects the item; the chips
   * are not individually interactive.
   *
   * @type {Array<String>}
   * @default []
   */
  tags: {
    type: Array,
    default: () => [],
  },
  /**
   * @type {Object}
   * @default {}
   */
  prependIconProps: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Icon URL to prepend to the list item.
   *
   * @type {String}
   * @default undefined
   */
  prependIconUrl: {
    type: String,
    default: undefined,
  },
  /**
   * Avatar to prepend to the list item, rendered as an `<sp-avatar>` (a circular
   * image/glyph) instead of a plain icon — for rows that represent a person or
   * place (e.g. a doctor photo, a location). Either a bare image-URL String, or a
   * descriptor forwarded to `sp-avatar`: `{ image, icon, color, fillColor, shape,
   * cover }`. When set it takes precedence over `prependIcon` / `prependIconUrl`.
   *
   * @type {String|Object}
   * @default undefined
   */
  prependAvatar: {
    type: [String, Object],
    default: undefined,
  },
  /**
   * Icon to prepend to the list item when active.
   * If not provided, the prepend icon will be the same as the prepend icon.
   *
   * @type {String}
   * @default undefined
   */
  activePrependIcon: {
    type: String,
    default: undefined,
  },
  /**
   * @type {String}
   * @default undefined
   */
  activePrependIconFillColor: {
    type: String,
    default: undefined,
  },
  /**
   * @type {String}
   * @default undefined
   */
  appendIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Render a rotating chevron caret in the append region instead of a static
   * `appendIcon`. Driven by a parent (e.g. an accordion / drilldown container)
   * that toggles `appendCaretExpanded` to animate the chevron between "▶"
   * (collapsed) and "▼" (expanded). Replaces the former
   * `--sp-comp-list-item-append-icon-rotation` CSS hook.
   *
   * @type {Boolean|String}
   * @default false
   */
  appendCaret: booleanAttribute(),
  /**
   * When `appendCaret` is set, points the chevron down (expanded) if truthy,
   * right (collapsed) otherwise.
   *
   * @type {Boolean|String}
   * @default false
   */
  appendCaretExpanded: booleanAttribute(),
  /**
   * Switch the chevron caret to the in-place toggle mapping: down (collapsed),
   * up (expanded). Without it the chevron is a drill-in affordance: right
   * (collapsed), down (expanded). No effect unless `appendCaret` is set; the
   * `disclosure` variant ignores it.
   *
   * @type {Boolean|String}
   * @default false
   */
  appendCaretToggle: booleanAttribute(),
  /**
   * Render the append caret as a dedicated interactive control (a button that
   * emits `append-action` and swallows the click) instead of a decorative
   * indicator. Lets a parent (accordion) make the row body selectable while the
   * caret alone toggles expansion. No effect unless `appendCaret` is set.
   *
   * @type {Boolean|String}
   * @default false
   */
  appendCaretInteractive: booleanAttribute(),
  /**
   * Accessible label for the interactive append-caret toggle button. Supply a
   * translated string from the consumer; `aria-expanded` conveys the state.
   *
   * @type {String}
   * @default undefined
   */
  appendCaretLabel: {
    type: String,
    default: undefined,
  },
  /**
   * Visual style of the append caret: `"chevron"` (default) renders the rotating
   * SpCaret used for drill-in navigation; `"disclosure"` renders a plus / minus
   * icon (`add-filled` collapsed, `subtract-filled` expanded) for in-place
   * expand-collapse toggles, per the 2025 redesign. No effect unless
   * `appendCaret` is set.
   *
   * @type {String}
   * @default "chevron"
   */
  appendCaretVariant: {
    type: String,
    default: "chevron",
    validator: (value) => ["chevron", "disclosure"].includes(value),
  },
  /**
   * If true, the list item must consider the left indentation if no prepend icon is present.
   *
   * @type {Boolean|String}
   * @default false
   */
  leftIndentation: booleanAttribute(),
  /**
   * If true, the list item must consider the right indentation if no append icon is present.
   *
   * @type {Boolean|String}
   * @default false
   */
  rightIndentation: booleanAttribute(),
  /**
   * @type {Boolean|String}
   * @default false
   */
  readonly: booleanAttribute(),
  /**
   * If true, the list item is rendered as disabled: greyed text, a
   * `not-allowed` cursor, `aria-disabled`, and no hover/focus/active styling.
   * Selection is blocked by `sp-list` (see the list's `isSelectable` gate).
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * If true, the list item content text will wrap to multiple lines
   * instead of being constrained to a single line.
   *
   * @type {Boolean|String}
   * @default false
   */
  wrap: booleanAttribute(),
});
